import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import { backend, GroupListData } from "../../services/backend";
import { UserAdminFormFillSchema, UserAdminFormFillType } from "../../forms/createUser";
import { RootState } from "../../state/reducer";

interface UserAdminFormModalProps {
  onSave: () => void;
  closeModal: () => void;
}

const UserCreateByAdminFormModal: React.FC<UserAdminFormModalProps> = ({
  onSave,
  closeModal,
}) => {
  const State = useSelector((state: RootState) => state);
  const session = State.session;
  const rights = State.rights;
  const [roles, setRoles] = useState<{ id: number; name: string }[]>([]);
  const [groups, setGroups] = useState<GroupListData[]>([]);
  const [formData, setFormData] = useState<UserAdminFormFillType>({
    email: "",
    password: "",
    inviter: undefined,
    role_id: "15",
    group_id: undefined,
    phone: "",
    first_name: "",
    last_name: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    backend.getRoleList().then((res) => {
      setRoles(res.roles.filter(role => role.rang > rights.rang));
      // Если role_id ещё не установлен, устанавливаем его в первую роль
      if (!formData.role_id && res.roles.length > 0) {
        setFormData((prev) => ({ ...prev, role_id: String(res.roles[0].id) }));
      }
    });
    backend.getGroupsList().then((res) => {
         setGroups(res.groups)
       }).catch((e) => {
         toast("Не удалось загрузить список групп")
       });
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === "phone" || name === "email") {
      backend.checkDataHold({
        [name] : value
      }).then((result) => {
        if (result.hold) {
          toast(`Пользователь с таким ${name === "phone" ? "номером телефона" : "адресом электроннной почты"} уже существует`)
        }
      }).catch(() => {
        toast("Не удалось проверить, есть ли уже такой пользователь")
      })
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  
  console.log("Submitting formData:", formData);

  const validationResult = UserAdminFormFillSchema.safeParse(formData);

  if (!validationResult.success) {
    const errorMap: Record<string, string> = {};
    validationResult.error.issues.forEach((issue) => {
      errorMap[issue.path[0]] = issue.message;
    });
    setErrors(errorMap);
    console.log("Ошибки валидации:", errorMap);
    return;
  }

  console.log("Валидация пройдена, отправка...");
  setErrors({});

  backend.createUserByAdmin({
    ...formData,
    role_id: Number(formData.role_id),
    personal: {
      phone: formData.phone,
      first_name: formData.first_name,
      last_name: formData.last_name,
    },
  }).then(() => {
    toast("Пользователь успешно создан");
    onSave();
    closeModal();  
  }).catch((error) => {
    console.error("Ошибка при создании пользователя:", error);
    toast("Ошибка при создании пользователя");   
  })
};

  if (!session?.id) {
    return null;
  }

  return (
    <div className="creationForm">
      <div className="modalHeading">
        <h3>Создание нового пользователя</h3>
      </div>
      <form onSubmit={handleSubmit} className="columnCenter">
        {/* Поле Email */}
        <div className="formField formRow">
          <label>
            Email<span className="requireSymbol">*</span>
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
          />
          {errors.email && <p className="errorText">{errors.email}</p>}
        </div>

        {/* Поле Пароль */}
        <div className="formField formRow">
          <label>
            Пароль<span className="requireSymbol">*</span>
          </label>
          <input
            type="password"
            name="password"
            value={formData.password}
            onChange={handleInputChange}
          />
          {errors.password && <p className="errorText">{errors.password}</p>}
        </div>

        {/* Поле Роль */}
        <div className="formField formRow">
          <label>
            Роль<span className="requireSymbol">*</span>
          </label>
          <select
            name="role_id"
            value={formData.role_id}
            onChange={handleInputChange}
          >
            {roles.map((role) => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))}
          </select>
          {errors.role_id && <p className="errorText">{errors.role_id}</p>}
        </div>

        <div className="formField formRow">
          <label htmlFor="title">Группа:</label>
          <select
              id="group_id"
              name="group_id"
              value={formData.group_id}
              onChange={(event) =>
                setFormData((prev) => ({ 
                  ...prev,
                  group_id: Number(event.target.value)
                }))
              }
            >
              <option value={undefined} disabled>не указана</option>
              {groups.map((item, index) => {
                return (
                  <option key={index} value={item.id}>
                    {item.name}
                  </option>
                );
              })}
            </select>
        </div>

        {/* Поле Телефон */}
        <div className="formField formRow">
          <label>
            Телефон<span className="requireSymbol">*</span>
          </label>
          <input
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
          />
          {errors.phone && <p className="errorText">{errors.phone}</p>}
        </div>

        {/* Поле Имя */}
        <div className="formField formRow">
          <label>
            Имя<span className="requireSymbol">*</span>
          </label>
          <input
            type="text"
            name="first_name"
            value={formData.first_name}
            onChange={handleInputChange}
          />
          {errors.first_name && <p className="errorText">{errors.first_name}</p>}
        </div>

        {/* Поле Фамилия */}
        <div className="formField formRow">
          <label>
            Фамилия<span className="requireSymbol">*</span>
          </label>
          <input
            type="text"
            name="last_name"
            value={formData.last_name}
            onChange={handleInputChange}
          />
          {errors.last_name && <p className="errorText">{errors.last_name}</p>}
        </div>

        {/* Кнопки */}
        <div className="flex between btnRow">
          <button className="filterButton" type="submit">
            Сохранить
          </button>
          <button onClick={closeModal} className="cancelButton" type="button">
            Отменить
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserCreateByAdminFormModal;